import React from "react";
import { createPortal } from "react-dom";
import { X, Calendar, MapPin } from "lucide-react";

const ProductDetailsModal = ({ product, onClose }) => {
  if (!product) return null;

  // Group inventory by Warehouse first, then list expiry details inside
  const warehouseGroups = {};

  (product.warehouses || []).forEach((w) => {
    const wId = w.location?._id || w.locationId || w.location || "unknown";
    const wName = w.location?.name || w.locationName || "Unknown Warehouse";
    const qty = Number(w.quantity) || 0;

    if (qty <= 0) return; // Skip zero/negative stock

    if (!warehouseGroups[wId]) {
      warehouseGroups[wId] = {
        wName,
        totalQty: 0,
        expiries: [],
        minExpiry: Infinity,
      };
    }

    let expiryDate = "No Expiry Date";
    const rawExpiryDate = w.expiryDate || null;
    if (w.expiryDate) {
      try {
        const d = new Date(w.expiryDate);
        if (!isNaN(d.getTime())) {
          expiryDate = d.toLocaleDateString();
        }
      } catch (err) {
        console.error("Invalid date:", w.expiryDate);
      }
    }

    const batchId = w.batchId && w.batchId !== "N/A" ? w.batchId : "";

    warehouseGroups[wId].expiries.push({
      expiryDate,
      rawExpiryDate,
      batchId,
      quantity: qty,
    });
    warehouseGroups[wId].totalQty += qty;

    const expTime = w.expiryDate ? new Date(w.expiryDate).getTime() : Infinity;
    if (expTime < warehouseGroups[wId].minExpiry) {
      warehouseGroups[wId].minExpiry = expTime;
    }
  });

  // Sort warehouses by nearest expiry first
  const warehouseList = Object.values(warehouseGroups).sort((a, b) => a.minExpiry - b.minExpiry);

  // Sort batches chronologically inside each warehouse
  warehouseList.forEach((wh) => {
    wh.expiries.sort((a, b) => {
      const dateA = a.rawExpiryDate ? new Date(a.rawExpiryDate).getTime() : Infinity;
      const dateB = b.rawExpiryDate ? new Date(b.rawExpiryDate).getTime() : Infinity;
      return dateA - dateB;
    });
  });

  const totalStock = warehouseList.reduce((sum, w) => sum + w.totalQty, 0);

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* HEADER (Cloned from Add Brand Modal) */}
        <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
          <h2 className="text-lg font-semibold">Product Expiry & Batch Details</h2>
          <button type="button" onClick={onClose} className="text-xl text-white hover:opacity-80 transition">
            ✕
          </button>
        </div>

        {/* BODY (Edge-to-Edge with no vertical spacing) */}
        <div className="flex-1 overflow-y-auto">
          {/* PRODUCT CARD (Stretches edge-to-edge with border-b) */}
          <div className="flex gap-4 items-center bg-slate-50 border-b border-slate-200 p-4 px-6 rounded-none">
            {product.thumbnail ? (
              <img
                src={`${process.env.REACT_APP_IMAGE_URL}/${product.thumbnail}`}
                alt={product.name}
                className="w-16 h-16 rounded-lg object-contain bg-white border border-slate-200"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg border border-blue-200">
                {product.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-slate-800 truncate">{product.name}</h3>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {product.brand?.name && (
                  <span className="bg-blue-50 text-blue-700 text-xs px-2.5 py-0.5 rounded-full border border-blue-100 font-medium">
                    {product.brand.name}
                  </span>
                )}
                {product.category?.name && (
                  <span className="bg-purple-50 text-purple-700 text-xs px-2.5 py-0.5 rounded-full border border-purple-100 font-medium">
                    {product.category.name}
                  </span>
                )}
                <span className="bg-slate-100 text-slate-700 text-xs px-2.5 py-0.5 rounded-full font-medium border border-slate-200">
                  {product.quantityValue} {product.quantityUnit}
                </span>
                <span className="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-0.5 rounded-full font-bold border border-emerald-100 shadow-sm">
                  Total Stock: {totalStock}
                </span>
              </div>
            </div>
          </div>

          {/* SECTION HEADER FOR STOCK INFO */}
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-2.5">
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Warehouse Stock Breakdown</h4>
          </div>

          {/* STOCK INFO - GROUPED BY WAREHOUSE (Stacked directly below the header) */}
          <div className="divide-y divide-slate-200 border-b border-slate-200 rounded-none">
            {warehouseList.length === 0 ? (
              <div className="text-center text-slate-500 py-8 text-sm bg-slate-50">
                No stock is currently available.
              </div>
            ) : (
              warehouseList.map((wh, idx) => (
                <div key={idx} className="divide-y divide-slate-150">
                  {/* WAREHOUSE HEADER ROW */}
                  <div className="flex justify-between items-center bg-slate-100 px-6 py-2.5 text-slate-800 font-bold text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-[#172554]" />
                      <span>{wh.wName}</span>
                    </div>
                    <span>Total Stock: {wh.totalQty}</span>
                  </div>

                  {/* EXPIRY BREAKDOWN LIST */}
                  {wh.expiries.map((exp, expIdx) => (
                    <div key={expIdx} className="flex justify-between items-center px-8 py-2.5 text-xs bg-white hover:bg-slate-50/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Calendar size={14} className="text-slate-400" />
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-slate-700">{exp.expiryDate}</span>
                          {exp.batchId && (
                            <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px] text-slate-500 border border-slate-200 shadow-sm">
                              Batch: {exp.batchId}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="bg-blue-50 text-blue-700 px-2.5 py-1 rounded text-xs font-bold border border-blue-100">
                        {exp.quantity} units
                      </span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        {/* FOOTER (Cloned from Add Brand Modal) */}
        <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductDetailsModal;
